import { getGlobalConfig } from '@core/globalConfig'
import { isTruthy } from '@core/helpers/boolean'
import { yieldToMain } from '@core/helpers/yieldToMain'
import { logError } from '@core/logging'
import { persistFWReferrer } from '@core/tracking/attribution'
import {
  persistBusinessMembershipId,
  persistGuestId,
  persistOauthAppId,
  persistUTM,
} from '@core/tracking/funnelTracking'
import { sendPixelTrackingWithSession } from '@core/tracking/senders'
import { getPageUrl } from '@core/window'

import { fwnAnalyticsGetter } from './getter'
import { patchDataLayer } from './patchDataLayer'

interface AnalyticsConfigs {
  shouldSendPageLoadPixelEvent?: boolean
  shouldPersistGuestId?: boolean
}

/**
 * Attaches the GA dataLayer tracking bridge, forwarding tracked ecommerce
 * events (add_to_cart, purchase) into the analytics module.
 *
 * Opt-in only: the bridge is attached exclusively when the `tracking_ga_event` global
 * config flag is enabled (e.g. `data-fwparam_tracking_ga_event="true"`).
 */
function attachGaTrackingBridge() {
  if (!isTruthy(getGlobalConfig().tracking_ga_event)) {
    return
  }

  try {
    patchDataLayer()
  } catch (e: unknown) {
    if (e instanceof Error) {
      logError(new Error('Failed to attach GA tracking bridge'), {
        captureContext: {
          extra: {
            errorMessage: e.message,
          },
        },
      })
    }
  }
}

async function initModule({
  shouldSendPageLoadPixelEvent = true,
  shouldPersistGuestId = true,
}: AnalyticsConfigs) {
  try {
    Object.defineProperty(window._fwn, 'analytics', {
      get: fwnAnalyticsGetter,
      configurable: false,
    })

    persistUTM()

    if (shouldPersistGuestId) {
      persistGuestId()
    }
    persistOauthAppId()
    persistBusinessMembershipId()

    const pageUrl = getPageUrl()
    const data: Record<string, string | null> = {}
    try {
      if (pageUrl) {
        persistFWReferrer(pageUrl)
        // We are sending fw_bmid as part of system:page_load to easily test
        // fw_bmid retention for user
        data.fw_bmid = new URL(pageUrl).searchParams.get('fw_bmid')
      }
    } catch (e) {}

    if (shouldSendPageLoadPixelEvent) {
      // For the analytics.js widget we don't want to send this event as the analytics.js
      // bundle is included on every page and very noisy, so this will only fire for fwn.js
      // script tags.

      await yieldToMain()

      sendPixelTrackingWithSession({
        endpoint: {
          url: '/trk/system:page_load',
          isUserEngagement: false,
          consent: 'essential',
        },
        data,
        host: undefined,
      })
    }
  } catch (e: unknown) {
    if (e instanceof Error) {
      logError(new Error('Failed to attach analytics module'), {
        captureContext: {
          extra: {
            errorMessage: e.message,
          },
        },
      })
    }
  }
}

/**
 * Attaches the analytics module.
 */
export function attach(configs: AnalyticsConfigs = {}) {
  if (typeof window === 'undefined') {
    return
  }

  attachGaTrackingBridge()

  if (!Object.prototype.hasOwnProperty.call(window._fwn, 'analytics')) {
    initModule(configs)
  } else {
    // If a fallback analytics module is already attached, we need to send the
    // queued events
    if (window._fwn.analytics.isFallback()) {
      const eventQueue = window._fwn.analytics.eventQueue
      initModule(configs)
      eventQueue.forEach(({ url, data, isUserEngagement }) => {
        sendPixelTrackingWithSession({
          endpoint: {
            url,
            consent: 'essential',
            isUserEngagement: !!isUserEngagement,
          },
          data,
          host: undefined,
        })
      })
    }
  }
}
